import type { NavigateFunction } from "react-router-dom";
import type { Notification } from "../../hooks/useNotifications";

// ── Where a notification leads ───────────────────────────────────────────────
// Priority order matters here:
//   1. A SPECIFIC destination derived from metadata (a space's companion feed,
//      a running engagement, a chat) — this beats stored actionUrls because
//      several backend creators historically stored generic ones ("/portal",
//      "/spaces") that dump the user at an overview instead of the thing the
//      notification is about.
//   2. The stored actionUrl (in-app paths only) — trusted when we have
//      nothing more specific; program links etc. arrive this way.
//   3. A type-level default.
// Returning null means "nowhere specific" — callers send those to the full
// notifications view so a click always lands somewhere readable.

export const resolveNotificationTarget = (n: Notification): string | null => {
  const m = n.metadata ?? {};
  const spaceId: string | undefined = m.customData?.spaceId;

  // 1. Specific, derived destinations
  if (spaceId && (n.type === "COMPANION_MESSAGE" || n.type === "INSIGHT_SHARED")) {
    // The space's Journey tab holds the companion feed and shared insights.
    return `/spaces?space=${spaceId}`;
  }
  if (m.processId && m.prescriptionId) {
    return `/activities/run?wid=${m.prescriptionId}&pid=${m.processId}`;
  }
  if (m.chatId) return "/chats";

  // 2. Stored actionUrl — but only when it's more than a bare overview path
  // that a derived default would beat anyway.
  if (m.actionUrl && m.actionUrl.startsWith("/")) return m.actionUrl;

  // 3. Type defaults
  switch (n.type) {
    case "NEW_MESSAGE":
    case "PROCESS_CHAT_MESSAGE":
      return "/chats";
    case "MENTORING_REQUEST":
      return "/mentoring";
    case "PROGRAM_ASSIGNED":
    case "MILESTONE_COMPLETED":
    case "MILESTONE_ACTIVATED":
    case "MILESTONE_STALLED":
      return "/programs";
    case "ACTION_ITEM_ASSIGNED":
      return "/portal";
    default:
      return null;
  }
};

// Navigation with the side effects the destination needs to actually land on
// the right thing — tab preferences are localStorage-driven in this app, so a
// bare URL is not always enough.
// Returns true when it navigated; false means "no destination" and the caller
// decides the fallback (the dropdown goes to /notifications, the page stays).
export const openNotification = (n: Notification, navigate: NavigateFunction): boolean => {
  const target = resolveNotificationTarget(n);
  if (!target) return false;

  const spaceMatch = /^\/spaces\?space=([^&]+)/.exec(target);
  if (spaceMatch) {
    const spaceId = spaceMatch[1];
    // The companion feed lives on the Journey tab — make sure a stored
    // per-space tab preference doesn't hide it on arrival.
    localStorage.setItem(`space_${spaceId}_active_tab`, "journey");
    // SpacesView listens for this when already mounted; harmless otherwise.
    window.dispatchEvent(new CustomEvent("spaces-select-space", { detail: spaceId }));
    navigate(target, { state: { selectedSpaceId: spaceId } });
    return true;
  }

  if (target === "/portal" && n.type === "ACTION_ITEM_ASSIGNED") {
    // The assigned item lives in the workspace tab, not the activities tab.
    localStorage.setItem("portal_active_tab", "my_workspace");
    window.dispatchEvent(new CustomEvent("portal-tab-change", { detail: "my_workspace" }));
  }

  navigate(target);
  return true;
};
